import React from 'react';
import PluginHeader from '../PluginHeader';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";

const EmailReportingPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Email reporting - Aronium</title>
          <meta name="description" content="Stay informed by receiving sales reports from your store directly to your inbox" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            Email reporting
          </h1>
          <p>
            Automate your sales reporting by scheduling daily sales reports to be sent directly to your email.
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <a
              href={`${BASE_URL}/plugins/email-reporting/latest/Aronium.Pos.Email.Reporting.Lite.exe`}
              className="text-white inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-rose-500 hover:bg-rose-700 transition duration-300 shadow-lg focus:ring-4 focus:ring-rose-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Lite
            </a>

            <a
              href={`${BASE_URL}/plugins/email-reporting/latest/Aronium.Pos.Email.Reporting.Pro.exe`}
              className="text-white inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-blue-400 hover:bg-blue-500 transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Pro
            </a>
          </div>
        </div>
      </section>

      <div className="flex flex-col text-center mt-12">
        <div className="grid place-content-center justify-items-center px-4">
          <img src="/images/email-reporting-settings-screen.jpg" className='rounded-t-lg xl:max-w-6xl' alt="Setup email reporting" />
        </div>
      </div>

      <section className='bg-gradient-to-tr from-sky-50 to-blue-100 py-12'>
        <PluginHeader price="3" monthlySubscriptionAvailable={false} note="* Annual subscription only" />
      </section>

      {/* Overview Section */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use <span className='font-semibold'>Email reporting</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 mx-auto">
            The <span className='font-semibold'>Email reporting</span> plugin enables businesses to receive automated sales reports via email, ensuring timely access to crucial sales data. Users can configure the plugin to send reports periodically throughout the day or at the end of the business day, facilitating efficient sales monitoring and decision-making.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="pb-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Customizable reporting</h3>
            <p className="mt-4 text-gray-600">
              Select specific reports to receive, such as X reports during the day and Z reports at day's end, in preferred formats like PDF or Excel.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Scheduled email delivery</h3>
            <p className="mt-4 text-gray-600">
              Set up periodic email intervals and define business hours for report delivery, with options to receive emails even when no sales have occurred.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-tr from-blue-400 to-indigo-400 py-24 text-center text-white">
        <div className="relative mx-auto px-4">
          <h2 className="text-3xl md:text-6xl mb-6">
            Ready to elevate your email reporting?
          </h2>
          <p className="text-xl mb-10">
            Start using the <span className='font-semibold'>Email reporting</span> plugin for streamlined reporting and enhanced communication.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/4418582651410-Send-daily-sales-reports-to-email"
            className="button-rounded-white px-12 py-4"
            target="_blank"
            rel="noreferrer">
            Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default EmailReportingPlugin;
